// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
// Fortschritt: Hero mit Trainingspflanze, KPIs, Meilensteine, Übungstabelle, Wochendiagramm, Export
// Portiert aus docs/referenz/trainingslog.html (#tab-stats) + Desktop-Dashboard
import { Fragment, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import type { Exercise, WorkoutEntry } from "@/lib/types"
import { pb } from "@/lib/pb"
import { getAllEntries, getExercises, onSynced, sofort } from "@/lib/data"
import { uniqueDates, totalVolume, unitsLast7Days, sparkline, volumeByBodyPart, isoWeek } from "@/lib/stats"
import { LEVELS, aktuellesLevel, levelFertig, questFertig, questStats } from "@/lib/quests"
import { best1RM } from "@/lib/records"
import { loadWeights, upsertWeight, type BodyWeight } from "@/lib/weight"
import { bodyPartDe } from "@/lib/labels"
import { fmtWeight } from "@/lib/sets"
import { fmtDate, todayISO } from "@/lib/dates"
import { useCountUp } from "@/lib/useCountUp"
import { naechsterVergleich, vergleichSatz } from "@/lib/vergleiche"
import { toast } from "@/lib/toast"
import VolumeChart from "@/components/stats/VolumeChart"
import WeightChart from "@/components/stats/WeightChart"
import Trainingspflanze from "@/components/stats/Trainingspflanze"
import Igel from "@/components/celebration/creatures/Igel"
import "@/styles/stats.css"

const feld = (exs: Exercise[] | null, key: "name" | "body_part"): Map<string, string> =>
  new Map((exs ?? []).map((x) => [x.id, x[key]]))

/** Feiert die Zahl statt Phrasen (Skill §8), nach Bereichen gestaffelt. */
function heroLine(units: number): string {
  if (units === 0) return "Einheit 1 wartet. Dein Rücken auch."
  if (units === 1) return "1 Einheit. Der Anfang ist gemacht."
  if (units < 5) return `${units} Einheiten. Der Anfang ist gemacht.`
  if (units < 25) return `${units} Einheiten. Dein Rücken merkt das.`
  return `${units} Einheiten. Das ist Beständigkeit.`
}

export default function StatsPage() {
  const navigate = useNavigate()
  // Sofort-Speicher: beim Tab-Wechsel steht die Seite schon (Runde 16)
  const [entries, setEntries] = useState<WorkoutEntry[]>(() => sofort.entries() ?? [])
  const [nameById, setNameById] = useState<Map<string, string>>(() => feld(sofort.exercises(), "name"))
  const [bodyPartById, setBodyPartById] = useState<Map<string, string>>(() => feld(sofort.exercises(), "body_part"))
  const [weights, setWeights] = useState<BodyWeight[]>([])
  const [kgInput, setKgInput] = useState("")
  const [savingWeight, setSavingWeight] = useState(false)

  useEffect(() => {
    const load = async () => {
      const [es, exs] = await Promise.all([getAllEntries(), getExercises()])
      setEntries(es)
      setNameById(feld(exs, "name"))
      setBodyPartById(feld(exs, "body_part"))
    }
    void load()
    return onSynced(() => { void load() })
  }, [])

  useEffect(() => {
    // online-only: offline bleibt die Kurve leer, das Formular nutzbar
    loadWeights().then(setWeights).catch(() => {})
  }, [])

  async function saveWeight() {
    const kg = Number(kgInput)
    if (!(kg > 0)) { toast("Bitte dein Gewicht in kg eingeben"); return }
    setSavingWeight(true)
    try {
      await upsertWeight(todayISO(), kg)
      setWeights(await loadWeights())
      setKgInput("")
      toast("Gewicht eingetragen ✓")
    } catch {
      toast("Das hat nicht geklappt. Nochmal?")
    } finally {
      setSavingWeight(false)
    }
  }

  const days = uniqueDates(entries)
  const units = days.length
  const last7 = unitsLast7Days(entries)
  const totalVol = totalVolume(entries)
  const lastDate = days.length ? fmtDate(days[days.length - 1]) : "–"

  const volSatz = vergleichSatz(totalVol)
  const volNaechst = naechsterVergleich(totalVol)

  const unitsAnim = useCountUp(units, "fortschritt-einheiten")
  const last7Anim = useCountUp(last7, "fortschritt-7tage")
  const volAnim = useCountUp(Math.round(totalVol), "fortschritt-volumen")

  // Wochenziel: Einheiten in der laufenden ISO-Woche gegen users.weekly_goal
  const weeklyGoal = Number(pb.authStore.record?.weekly_goal ?? 0)
  const thisWeek = isoWeek(todayISO())
  const doneThisWeek = days.filter((d) => {
    const w = isoWeek(d)
    return w.year === thisWeek.year && w.week === thisWeek.week
  }).length

  const qStats = questStats(entries)
  const lvl = aktuellesLevel(qStats)

  const exerciseIds = [...new Set(entries.map((e) => e.exercise))]
  exerciseIds.sort((a, b) => (nameById.get(a) ?? a).localeCompare(nameById.get(b) ?? b, "de"))

  return (
    <>
      <div className="stats-grid">
        <div className="stats-col">
          <div className="card--hero stats-hero">
            <div className="hero-kpi">
              <b className="num-hero">{unitsAnim}</b>
              <span className="unit">{units === 1 ? "Einheit" : "Einheiten"}</span>
            </div>
            <Trainingspflanze units={units} />
            <p className="hero-line">{heroLine(units)}</p>
          </div>

          {/* Label-über-Zahl statt Kärtchen (Skill §13.2) */}
          <h2>Deine Zahlen</h2>
          <div className="statgrid">
            <div className="stat">
              <span className="stat-lbl">Letzte 7 Tage</span>
              <b className="num-hero">{last7Anim}</b>
            </div>
            <div className="stat">
              <span className="stat-lbl">Gesamtvolumen</span>
              <b className="num-hero">{volAnim.toLocaleString("de-DE")}<i>kg</i></b>
            </div>
            <div className="stat">
              <span className="stat-lbl">Letztes Training</span>
              {/* ohne Jahr — die Spalte ist schmal, das Jahr sagt nichts */}
              <b className="num-hero stat-date">{lastDate.replace(/\.\d{4}$/, ".")}</b>
            </div>
          </div>

          {/* Kilo in etwas übersetzen, das man sich vorstellen kann (Runde 16) */}
          {volSatz && (
            <p className="vergleich">
              <b>{volSatz}</b>
              {volNaechst && (
                <span> Noch {volNaechst.fehlen.toLocaleString("de-DE")} kg, dann ist es {volNaechst.ziel.eins}.</span>
              )}
            </p>
          )}

          {weeklyGoal > 0 && (
            <>
              <h2>Wochenziel</h2>
              {/* Ring statt Punkte (Apple-Muster, Runde 4) — feiern, nie bestrafen */}
              <div className="wg">
                <div
                  className="wg-ring"
                  role="img"
                  aria-label={`Diese Woche ${doneThisWeek} von ${weeklyGoal} Einheiten`}
                >
                  <svg width="88" height="88" viewBox="0 0 88 88" aria-hidden="true">
                    <circle className="wg-track" cx="44" cy="44" r="34" />
                    <circle
                      className="wg-progress"
                      cx="44" cy="44" r="34"
                      transform="rotate(-90 44 44)"
                      style={{ strokeDashoffset: 213.6 * (1 - Math.min(1, doneThisWeek / weeklyGoal)) }}
                    />
                  </svg>
                  <b>{doneThisWeek}/{weeklyGoal}</b>
                </div>
                <div className="wg-tx">
                  <p className="wg-line">Diese Woche {doneThisWeek} von {weeklyGoal} Einheiten.</p>
                  {doneThisWeek >= weeklyGoal
                    ? <p className="wg-done">Wochenziel geschafft. Stark.</p>
                    : <p className="wg-rest">Noch {weeklyGoal - doneThisWeek} — dann schließt sich der Ring.</p>}
                </div>
              </div>
            </>
          )}

          {/* Level & Subquests (Runde 8): kleine Aufgaben statt einer flachen
              Meilenstein-Liste — Bestwerte, nichts wird je aberkannt */}
          <h2>Dein Level</h2>
          <div className="lvl-kopf">
            <b className="lvl-num">{lvl.stufe}</b>
            <div className="lvl-tx">
              <span className="lvl-name">{lvl.name}</span>
              <span className="lvl-sub">
                {lvl.quests.filter((qu) => questFertig(qu, qStats)).length}/{lvl.quests.length} Aufgaben geschafft
              </span>
            </div>
          </div>
          <div className="ms-list">
            {LEVELS.map((level) => {
              const fertig = levelFertig(level, qStats)
              if (level.stufe !== lvl.stufe) {
                return (
                  <div key={level.stufe} className={fertig ? "ms done" : "ms lvl-fern"}>
                    <div className="dot">{fertig ? "✓" : level.stufe}</div>
                    <div className="t">Level {level.stufe} – {level.name}</div>
                    <div className="s">{fertig ? "geschafft" : "kommt noch"}</div>
                  </div>
                )
              }
              return (
                <Fragment key={level.stufe}>
                  <div className="ms lvl-jetzt">
                    <div className="dot">{level.stufe}</div>
                    <div className="t">Level {level.stufe} – {level.name}</div>
                    <div className="s">jetzt dran</div>
                  </div>
                  {level.quests.map((quest) => {
                    const wert = Math.min(quest.wert(qStats), quest.ziel)
                    const geschafft = wert >= quest.ziel
                    return (
                      <div key={quest.id} className={geschafft ? "quest done" : "quest"}>
                        <div className="q-dot">{geschafft ? "✓" : ""}</div>
                        <div className="q-t">{quest.titel}</div>
                        <div className="q-s">
                          {geschafft
                            ? "geschafft"
                            : `${wert.toLocaleString("de-DE")} / ${quest.ziel.toLocaleString("de-DE")}${quest.einheit ? ` ${quest.einheit}` : ""}`}
                        </div>
                        <div className="q-bar" aria-hidden="true">
                          <span style={{ width: `${Math.round((wert / quest.ziel) * 100)}%` }} />
                        </div>
                      </div>
                    )
                  })}
                </Fragment>
              )
            })}
          </div>
        </div>

        <div className="stats-col stats-right">
          <div className="sec-table">
            <h2>Fortschritt je Übung</h2>
            <div className="card" style={{ overflowX: "auto" }}>
              <table className="prog">
                <thead>
                  <tr><th>Übung</th><th>Sätze</th><th>Max kg</th><th>1RM≈</th><th>Trend</th></tr>
                </thead>
                <tbody>
                  {!exerciseIds.length && (
                    <tr>
                      <td colSpan={5}>
                        <div className="empty">
                          <div className="critter"><Igel /></div>
                          <p><b>Noch kein Eintrag. Dein erster Satz wartet.</b></p>
                          <button className="btn-secondary" onClick={() => navigate("/log")}>Training eintragen</button>
                        </div>
                      </td>
                    </tr>
                  )}
                  {exerciseIds.map((id) => {
                    const list = entries.filter((e) => e.exercise === id)
                    const nSets = list.reduce((s, e) => s + (e.sets ?? []).length, 0)
                    const maxW = Math.max(...list.flatMap((e) => (e.sets ?? []).map((s) => s.weight_kg || 0)), 0)
                    const e1rm = best1RM(list, id)
                    return (
                      <tr key={id}>
                        <td>{nameById.get(id) ?? id}</td>
                        <td className="n">{nSets}</td>
                        <td className="n">{maxW ? fmtWeight(maxW) : "–"}</td>
                        <td className="n">{e1rm ? fmtWeight(e1rm) : "–"}</td>
                        <td className="spark">{sparkline(list)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <p className="hint">Trend zeigt das schwerste Gewicht der letzten Einheiten. 1RM≈ schätzt, was du einmal sauber schaffen würdest.</p>
            </div>
          </div>

          <div className="sec-chart">
            <h2>Volumen je Woche</h2>
            <div className="card">
              <VolumeChart entries={entries} />
            </div>
          </div>

          <div>
            <h2>Volumen je Muskelgruppe</h2>
            <div className="card">
              {(() => {
                const rows = volumeByBodyPart(entries, bodyPartById)
                if (!rows.length) return <div className="empty">Füllt sich mit deinem ersten Satz.</div>
                const max = rows[0].volume
                const spitze = vergleichSatz(max)
                const spitzeName = rows[0].bodyPart === "sonstiges" ? "Sonstiges" : bodyPartDe(rows[0].bodyPart)
                return (
                  <>
                    {rows.map((r) => (
                      <div className="bp-row" key={r.bodyPart}>
                        <span className="bp-label">{r.bodyPart === "sonstiges" ? "Sonstiges" : bodyPartDe(r.bodyPart)}</span>
                        <span className="bp-bar"><span style={{ width: `${Math.max(4, (r.volume / max) * 100)}%` }} /></span>
                        <span className="bp-val">{Math.round(r.volume).toLocaleString("de-DE")} kg</span>
                      </div>
                    ))}
                    {spitze && (
                      <p className="hint" style={{ marginTop: 12 }}>
                        Spitzenreiter {spitzeName}: {spitze.replace("So viel wiegt ", "das wiegt ").replace("So viel wiegen ", "das wiegen ")}
                      </p>
                    )}
                  </>
                )
              })()}
            </div>
          </div>

          <div>
            <h2>Körpergewicht</h2>
            <div className="card">
              <form
                className="weight-form"
                onSubmit={(e) => { e.preventDefault(); void saveWeight() }}
              >
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="20"
                  max="400"
                  aria-label="Körpergewicht in kg"
                  placeholder="kg, z. B. 82.5"
                  value={kgInput}
                  onChange={(e) => setKgInput(e.target.value)}
                />
                <button type="submit" className="btn-secondary pressable" disabled={savingWeight}>
                  {savingWeight ? "Speichert…" : "Eintragen"}
                </button>
              </form>
              {weights.length
                ? <WeightChart weights={weights} />
                : <p className="hint">Trag dein Gewicht ein, die Kurve wächst mit.</p>}
            </div>
          </div>

        </div>
      </div>

      <footer>fitHub · Deine Daten bleiben auf unserem eigenen Server.</footer>
    </>
  )
}
